import { Alert, CircularProgress, Stack } from '@mui/material';
import { Suspense, lazy, type ComponentType } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { createAuthorizationHeaders } from '../../api/httpClient';
import RemoteBoundary from '../../components/AppLayout/RemoteBoundary';
import { useUserData } from '../../redux/selectors';
import { fetchUserData } from '../../redux/user/actions';

interface OnboardingApiConfig {
  authV3Base: string;
  userEmail: string;
  getAuthHeaders?: () => Promise<HeadersInit | undefined>;
}

interface OnboardingWelcomePageProps {
  apiConfig?: OnboardingApiConfig;
  submissionErrorText?: string;
  onSubmissionSuccess?: () => Promise<void> | void;
  locale?: string;
}

const OnboardingWelcomePage = lazy<ComponentType<OnboardingWelcomePageProps>>(
  () => import('onboardingMicrofrontend/OnboardingWelcomePage')
);

const OnboardingWelcome = (): React.JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useUserData();
  const authV3Base = import.meta.env.VITE_AUTH_V3_BASE;

  const apiConfig: OnboardingApiConfig = {
    authV3Base,
    userEmail: user.email,
    getAuthHeaders: createAuthorizationHeaders,
  };

  const handleSubmissionSuccess = (): void => {
    dispatch(fetchUserData({ email: user.email }));
  };

  return (
    <RemoteBoundary
      name='OnboardingWelcomePage'
      microfrontend='onboarding-mf'
      fallback={
        <Alert severity='warning'>{intl.formatMessage({ id: 'onboardingUnavailable' })}</Alert>
      }
    >
      <Suspense
        fallback={
          <Stack
            alignItems='center'
            padding={4}
          >
            <CircularProgress />
          </Stack>
        }
      >
        <OnboardingWelcomePage
          apiConfig={apiConfig}
          onSubmissionSuccess={handleSubmissionSuccess}
          submissionErrorText={intl.formatMessage({ id: 'onboardingSubmissionFailed' })}
          locale={user.locale ?? 'en'}
        />
      </Suspense>
    </RemoteBoundary>
  );
};

export default OnboardingWelcome;
